// @ts-nocheck
import React, { useMemo, useState } from 'react';
import { Sparkles, Search, Calendar, ListChecks, ArrowRight } from 'lucide-react';

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Landing view for the QoE Workbench tab.
 *
 * Shown when no engagement is currently selected — lists every mapping
 * session whose qoe_report is attached. Clicking a row hands the
 * mapping_session_id back to the parent, which sets currentMapperSessionId
 * and re-renders QoEWorkbench with the workbench surface.
 */
const QoEWorkbenchLanding = ({ sessions = [], loading = false, onSelect }) => {
  const [search, setSearch] = useState('');

  const qoeSessions = useMemo(() => {
    const filtered = (sessions || []).filter((s) => s.has_qoe_report);
    const q = search.trim().toLowerCase();
    const matched = q
      ? filtered.filter((s) => (s.name || '').toLowerCase().includes(q))
      : filtered;
    return matched
      .slice()
      .sort(
        (a, b) =>
          // Pinned (saved) engagements float; then most recent first.
          Number(b.is_pinned || 0) - Number(a.is_pinned || 0) ||
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [sessions, search]);

  return (
    <div className="qoe-wb-landing">
      <div className="qoe-wb-landing-header">
        <div className="qoe-wb-landing-icon-circle">
          <ListChecks size={22} />
        </div>
        <div>
          <div className="qoe-wb-eyebrow">QoE Workbench</div>
          <h1 className="qoe-wb-title">Saved engagements</h1>
          <p className="qoe-wb-landing-subtitle">
            Pick a saved mapping session to review its flagged adjustments,
            build the Adjusted EBITDA bridge, and produce the diligence punch
            list. Sessions appear here once a QoE report has been attached.
          </p>
        </div>
      </div>

      {loading && !sessions.length ? (
        <div className="qoe-wb-loading">
          <div className="spinner"></div>
          <p>Loading engagements…</p>
        </div>
      ) : sessions.length > 0 && qoeSessions.length === 0 ? (
        <div className="qoe-wb-landing-empty">
          <p>
            <strong>No QoE engagements yet.</strong>
          </p>
          <p>
            To start one: open a session in <strong>Mapper</strong>, run the QoE
            candidates flow, and the workbench will be available here as soon as
            the report is attached.
          </p>
        </div>
      ) : (
        <>
          <div className="qoe-wb-landing-search">
            <Search size={14} />
            <input
              type="text"
              placeholder="Search engagements by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="qoe-wb-landing-table-wrap">
            <table className="qoe-wb-landing-table">
              <thead>
                <tr>
                  <th>Engagement</th>
                  <th>Created</th>
                  <th className="amount">Accounts</th>
                  <th className="amount">Periods</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {qoeSessions.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => onSelect && onSelect(s.id)}
                    title="Open in workbench"
                  >
                    <td>
                      <div className="qoe-wb-landing-name">
                        <Sparkles size={12} className="qoe-wb-landing-sparkle" />
                        <span>{s.name || 'Untitled engagement'}</span>
                        {s.is_pinned && (
                          <span className="qoe-wb-landing-pinned" title="Pinned">
                            ★
                          </span>
                        )}
                        {s.shared_by_email && (
                          <span
                            className="qoe-wb-landing-shared"
                            title={`Shared with you by ${s.shared_by_email}`}
                          >
                            Shared by {s.shared_by_email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="qoe-wb-landing-meta">
                        <Calendar size={11} /> {fmtDate(s.created_at)}
                      </span>
                    </td>
                    <td className="amount">{s.account_count || '—'}</td>
                    <td className="amount">{s.period_count || '—'}</td>
                    <td className="qoe-wb-landing-cta">
                      <span className="qoe-wb-landing-open">
                        Open <ArrowRight size={12} />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default QoEWorkbenchLanding;
